import React, { useState, useEffect, Suspense, lazy } from 'react';
import styled, { ThemeProvider } from 'styled-components';
import { darkTheme } from './utils/Themes';
import Navbar from './components/sections/Navbar';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import Preloader from './components/sections/Preloader';
import CustomCursor from './components/CustomCursor';
import ReactHowler from 'react-howler';
import bgm from './images/bgm.mp3';
import ScrollManager from './utils/ScrollManager';
import SmoothScroll from './components/SmoothScroll';
import Home from './pages/Home';

import { VolumeUp, VolumeOff } from './components/Icons';

const ProjectsPage = lazy(() => import('./pages/ProjectsPage'));

const Body = styled.div`
  background-color: ${({ theme }) => theme.bg};
  color: ${({ theme }) => theme.text_primary};
  width: 100%;
  min-height: 100svh;
  overflow-x: clip;
  position: relative;
`;

const MuteButton = styled.button`
  position: fixed;
  bottom: calc(16px + var(--safe-bottom));
  right: calc(16px + var(--safe-right));
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  padding: 0;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.7);
  border: none;
  cursor: pointer;
  font-size: 22px;
  z-index: 100;
  transition: background-color 0.2s ease, transform 0.2s ease;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.text_primary};
    outline-offset: 2px;
  }

  @media (hover: hover) {
    &:hover {
      background-color: rgba(255, 255, 255, 0.9);
      transform: scale(1.05);
    }
  }
`;


function App() {
  const [isMuted, setIsMuted] = useState(false);
  const [musicStarted, setMusicStarted] = useState(false);

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  // Background music only loads after the first user interaction,
  // so it never competes with the initial page / 3D load.
  useEffect(() => {
    const start = () => setMusicStarted(true);
    window.addEventListener('pointerdown', start, { once: true });
    window.addEventListener('keydown', start, { once: true });
    return () => {
      window.removeEventListener('pointerdown', start);
      window.removeEventListener('keydown', start);
    };
  }, []);

  return (
    <>
      {/* <div className="grain-overlay"></div> */}
      <CustomCursor />
      <Preloader />
      <ThemeProvider theme={darkTheme}>
        <BrowserRouter>
          <SmoothScroll />
          <ScrollManager />
          <Navbar />
          <Body id="scroll-container">
            <Suspense fallback={null}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/projects" element={<ProjectsPage />} />
              </Routes>
            </Suspense>
          </Body>
        </BrowserRouter>
      </ThemeProvider>
      <ReactHowler
        src={bgm}
        playing={musicStarted}
        loop={true}
        volume={0.2}
        preload={false}
        mute={isMuted}
      />
      <MuteButton
        type="button"
        onClick={toggleMute}
        aria-label={isMuted ? 'Unmute background music' : 'Mute background music'}
        aria-pressed={isMuted}
      >
        {isMuted ? <VolumeOff fontSize="inherit" /> : <VolumeUp fontSize="inherit" />}
      </MuteButton>

    </>
  );
}

export default App;
